import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Upload, History, BookOpenCheck } from "lucide-react";

export default function AdminCorpus() {
  const [versions, setVersions] = useState([]);
  const [lex, setLex] = useState({ fr: [], en: [] });
  const [notes, setNotes] = useState("");
  const [lang, setLang] = useState("fr");
  const [filter, setFilter] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const [v, a] = await Promise.all([adminApi.get("/corpus"), adminApi.get("/corpus/active")]);
      setVersions(v.data);
      setLex({ fr: a.data.fr, en: a.data.en });
    } catch { toast.error("Erreur chargement corpus"); }
  };
  useEffect(() => { load(); }, []);

  const rows = lex[lang].filter((r) => !filter || r.term.includes(filter.toLowerCase()) || r.category.includes(filter.toLowerCase()));
  const upd = (term, k, v) => setLex((l) => ({ ...l, [lang]: l[lang].map((r) => (r.term === term ? { ...r, [k]: v } : r)) }));
  const del = (term) => setLex((l) => ({ ...l, [lang]: l[lang].filter((r) => r.term !== term) }));
  const add = () => setLex((l) => ({ ...l, [lang]: [{ term: "", severity: "orange", category: "generic_green", _new: true }, ...l[lang]] }));
  const updNew = (i, k, v) => setLex((l) => ({ ...l, [lang]: l[lang].map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));

  const publish = async () => {
    setPublishing(true);
    try {
      const clean = (arr) => arr.filter((r) => r.term.trim()).map(({ term, severity, category }) => ({ term, severity, category }));
      const { data } = await adminApi.post("/corpus", { notes, fr: clean(lex.fr), en: clean(lex.en) });
      setResult(data.reeval); setNotes("");
      toast.success(`Corpus v${data.version} publié — ${data.reeval.claims_changed} allégation(s) ré-évaluée(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setPublishing(false); }
  };

  const activate = async (id) => {
    try {
      const { data } = await adminApi.post(`/corpus/${id}/activate`);
      setResult(data.reeval);
      toast.success(`v${data.version} activée`);
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-corpus-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Administration</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><BookOpenCheck className="w-7 h-7 text-emerald-400" /> Corpus légal versionné</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <section className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="inline-flex rounded-md border border-slate-800 overflow-hidden">
              {["fr", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)}
                  className={`px-4 py-2 text-xs font-medium ${lang === l ? "bg-[#1F5A99] text-white" : "text-slate-400 hover:bg-slate-800"}`}
                  data-testid={`corpus-lang-${l}`}>
                  {l.toUpperCase()} · {lex[l].length}
                </button>
              ))}
            </div>
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer…"
              className="h-9 max-w-xs bg-[#0b1220] border border-slate-700 rounded-md px-3 text-xs" data-testid="corpus-filter" />
            <button onClick={add} className="h-9 ml-auto inline-flex items-center gap-1 px-3 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs" data-testid="corpus-add-btn">
              <Plus className="w-3.5 h-3.5" /> Terme
            </button>
          </div>
          <div className="max-h-[520px] overflow-auto border border-slate-800 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-[#0b1220] sticky top-0 uppercase tracking-widest text-[10px] text-slate-500">
                <tr><th className="p-3 text-left">Terme</th><th className="p-3 w-32 text-left">Sévérité</th><th className="p-3 w-48 text-left">Catégorie</th><th className="p-3 w-10"></th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r._new ? `new-${i}` : r.term} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`corpus-row-${r.term || "new"}`}>
                    <td className="p-2">{r._new
                      ? <input value={r.term} onChange={(e) => updNew(i, "term", e.target.value)} placeholder="nouveau terme"
                          className="h-8 bg-[#0b1220] border border-slate-700 rounded-md px-2 text-xs w-full" data-testid="corpus-new-term" />
                      : <span className="font-medium text-slate-200">{r.term}</span>}</td>
                    <td className="p-2">
                      <select value={r.severity} onChange={(e) => (r._new ? updNew(i, "severity", e.target.value) : upd(r.term, "severity", e.target.value))}
                        className={`h-8 rounded-md border px-2 text-[11px] font-mono uppercase ${r.severity === "red" ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                        <option value="red">red</option><option value="orange">orange</option>
                      </select>
                    </td>
                    <td className="p-2"><input value={r.category} onChange={(e) => (r._new ? updNew(i, "category", e.target.value) : upd(r.term, "category", e.target.value))}
                      className="h-8 bg-[#0b1220] border border-slate-700 rounded-md px-2 text-xs font-mono w-full" /></td>
                    <td className="p-2 text-right"><button onClick={() => (r._new ? setLex((l) => ({ ...l, [lang]: l[lang].filter((_, idx) => idx !== i) })) : del(r.term))}
                      className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 grid md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Notes de version</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex. : ajout « régénératif » (rouge)…"
                className="min-h-[60px] w-full bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" data-testid="corpus-notes" />
            </div>
            <button onClick={publish} disabled={publishing}
              className="h-11 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-5 rounded-md text-sm" data-testid="corpus-publish-btn">
              <Upload className="w-4 h-4" /> {publishing ? "Publication…" : "Publier"}
            </button>
          </div>
          {result && (
            <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-xs text-emerald-300" data-testid="corpus-reeval-result">
              <b>Ré-évaluation :</b> {result.claims_total} allégation(s) analysée(s), <b>{result.claims_changed}</b> modifiée(s), {result.users_notified} client(s) alerté(s).
            </div>
          )}
        </section>

        <aside className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-emerald-400" />
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Historique versions</p></div>
          <ul className="space-y-3" data-testid="corpus-versions">
            {versions.map((v) => (
              <li key={v.id} className={`p-3 rounded-md border ${v.active ? "border-emerald-500/40 bg-emerald-500/10" : "border-slate-800"}`} data-testid={`corpus-version-${v.version}`}>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-white">v{v.version}</span>
                  {v.active
                    ? <span className="px-2 py-0.5 text-[10px] rounded-full font-mono bg-emerald-500 text-slate-900">ACTIVE</span>
                    : <button onClick={() => activate(v.id)} className="text-xs text-emerald-400 hover:underline" data-testid={`corpus-activate-${v.version}`}>Réactiver</button>}
                </div>
                <p className="text-xs text-slate-500 mt-1">{v.notes || "—"}</p>
                <p className="font-mono text-[10px] text-slate-600 mt-1">{v.created_at.slice(0, 16).replace("T", " ")} · {v.created_by}{v.reeval ? ` · ${v.reeval.claims_changed} modif.` : ""}</p>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
